import { useCallback, useEffect, useRef } from 'react';
import { type SerializedStyles } from '@emotion/react';
import { useInView } from 'react-intersection-observer';
import { useTranslation } from '@frontend/i18n';
import { useOnboardingShallowStore } from '@frontend/product-onboarding';
import { FeatureId, useEligibility, usePreference, type EligibilityCriteria } from '@frontend/user-preferences';
import { CI_PRO_FEATURE_IDS } from '../../constant';
import { useProOnboardingEligibility } from '../../hooks/use-pro-onboarding-eligibility';
import { CI_PRO_ONBOARDING_EVENTS, CI_PRO_ONBOARDING_TRACKING_IDS, trackOnboardingEvent } from '../../tracking';
import { hasReachedViewLimit } from '../../utils/banner.utils';
import { getCurrentTimestamp } from '../../utils/onboarding-lifecycle';
import { AnnouncementBannerUI } from './announcement-banner-ui';

const PRO_BANNER_CRITERIA: EligibilityCriteria = {
  strategy: 'any',
  conditions: [
    { type: 'view-count', maxViews: 4 },
    { type: 'click-count', maxClicks: 1 },
  ],
};

interface ProAnnouncementBannerProps {
  containerStyles?: SerializedStyles;
  onVisibilityChange?: (isVisible: boolean) => void;
}

type BannerActionSource = 'banner-click' | 'banner-dismiss' | 'banner-auto-dismiss';

export function ProAnnouncementBanner({ containerStyles, onVisibilityChange }: ProAnnouncementBannerProps) {
  const { isEligible, isLoading } = useProOnboardingEligibility();

  const isBannerEligible = !isLoading && isEligible;

  useEffect(() => {
    /**
     * When eligibility is still loading or fails, make sure the consuming page
     * does not keep suppressing any other banner.
     */
    if (!isBannerEligible) {
      onVisibilityChange?.(false);
    }
  }, [isBannerEligible, onVisibilityChange]);

  if (!isBannerEligible) return null;

  return <ProAnnouncementBannerContent containerStyles={containerStyles} onVisibilityChange={onVisibilityChange} />;
}

function ProAnnouncementBannerContent({ containerStyles, onVisibilityChange }: ProAnnouncementBannerProps) {
  const { t } = useTranslation('call-intel-onboarding');
  const autoOpenHandled = useRef(false);
  const bannerSeenTracked = useRef(false);

  const { isGuideOpen, toggleOnboardingGuide } = useOnboardingShallowStore('isGuideOpen', 'toggleOnboardingGuide');

  const [nudgeLifecycle, setNudgeLifecycle] = usePreference('CIProChecklistNudgeLifecycle');

  const { shouldShow, disableEligibility, recordClick, eligibilityData } = useEligibility({
    featureId: CI_PRO_FEATURE_IDS.announcementBanner as FeatureId,
    defaultCriteria: PRO_BANNER_CRITERIA,
  });

  const openGuideFromAnnouncement = useCallback(
    (source: BannerActionSource) => {
      if (!nudgeLifecycle.initialAutoOpenedAt) {
        setNudgeLifecycle({
          ...nudgeLifecycle,
          initialAutoOpenedAt: getCurrentTimestamp(),
        });
      }

      trackOnboardingEvent(CI_PRO_ONBOARDING_EVENTS.checklistAutoOpened, {
        source,
      });

      if (!isGuideOpen) {
        toggleOnboardingGuide();
      }
    },
    [nudgeLifecycle, setNudgeLifecycle, isGuideOpen, toggleOnboardingGuide]
  );

  const handleBannerVisibilityChange = useCallback(
    (inView: boolean) => {
      onVisibilityChange?.(inView);

      if (!inView) return;
      if (bannerSeenTracked.current) return;

      bannerSeenTracked.current = true;
      trackOnboardingEvent(CI_PRO_ONBOARDING_EVENTS.bannerSeen);
    },
    [onVisibilityChange]
  );

  const { ref: bannerRef } = useInView({
    threshold: 0,
    onChange: handleBannerVisibilityChange,
  });

  useEffect(() => {
    /**
     * If the banner lifecycle says it should no longer render, reset page-level
     * visibility so the backfill banner is not suppressed.
     *
     * Actual visible state is handled by useInView.
     */
    if (!shouldShow) {
      onVisibilityChange?.(false);
    }

    return () => {
      onVisibilityChange?.(false);
    };
  }, [shouldShow, onVisibilityChange]);

  useEffect(() => {
    if (autoOpenHandled.current) return;
    if (eligibilityData?.disable) return;

    const hasReachedAutoOpenLimit = hasReachedViewLimit(eligibilityData?.criteria);

    if (!hasReachedAutoOpenLimit) return;

    autoOpenHandled.current = true;

    disableEligibility();
    openGuideFromAnnouncement('banner-auto-dismiss');
  }, [eligibilityData, disableEligibility, openGuideFromAnnouncement]);

  const handleBannerAction = useCallback(
    (action: BannerActionSource) => {
      recordClick();
      disableEligibility();
      onVisibilityChange?.(false);
      openGuideFromAnnouncement(action);
    },
    [recordClick, disableEligibility, onVisibilityChange, openGuideFromAnnouncement]
  );

  if (!shouldShow) return null;

  return (
    <AnnouncementBannerUI
      ref={bannerRef}
      badgeLabel={t('New')}
      title={t('Ready to make a difference with Call Intelligence?')}
      description={t(
        'Track how your team handles scheduling conversations, follow up on calls where appointments were missed, and keep a pulse on patient sentiment to catch problems before they grow.'
      )}
      primaryActionLabel={t('Show Me Around')}
      onPrimaryAction={() => handleBannerAction('banner-click')}
      dismissActionLabel={t('Dismiss')}
      onDismissAction={() => handleBannerAction('banner-dismiss')}
      containerStyles={containerStyles}
      tracking={{
        primaryActionTrackingId: CI_PRO_ONBOARDING_TRACKING_IDS.bannerShowMeAroundClick,
        dismissActionTrackingId: CI_PRO_ONBOARDING_TRACKING_IDS.bannerDismissClick,
      }}
    />
  );
}
